import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useCreateCalendarEventFromScratch, useUser } from '@/hooks';
import { useMemberExternalAccounts } from '@/hooks/useMemberExternalAccountQueries';
import { composerContainerClassName } from '@/lib/composer-position';
import { useEventComposerStore } from '@/store/useEventComposerStore';
import { useSpeak2MeStore } from '@/store/speak2meStore';
import { useViewContextStore } from '@/store/viewContextStore';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import type { MemberExternalAccountSafe } from '@vivport/backend/types';
import { ChevronDown, Loader2, Minus, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const PROVIDER_LABEL: Record<MemberExternalAccountSafe['provider'], string> = {
  GOOGLE: 'Google',
  MICROSOFT: 'Microsoft',
};

function providerLabel(provider: MemberExternalAccountSafe['provider']): string {
  return PROVIDER_LABEL[provider] ?? provider;
}

function describeAccount(account: MemberExternalAccountSafe): string {
  return `${account.externalEmail} (${providerLabel(account.provider)})`;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value);
}

function localToIso(value: string, isAllDay: boolean): string {
  return new Date(isAllDay ? `${value}T00:00:00` : value).toISOString();
}

export default function EventComposer() {
  const isOpen = useEventComposerStore((s) => s.isOpen);
  const isMinimized = useEventComposerStore((s) => s.isMinimized);
  const draft = useEventComposerStore((s) => s.draft);
  const setDraft = useEventComposerStore((s) => s.setDraft);
  const closeEventCompose = useEventComposerStore((s) => s.closeEventCompose);
  const minimize = useEventComposerStore((s) => s.minimize);
  const restore = useEventComposerStore((s) => s.restore);

  const neoOpen = useSpeak2MeStore((s) => s.isOpen);
  const navigate = useNavigate();
  const { data: memberContext } = useUser();
  const memberId = memberContext?.selectedMember.id ?? 0;
  const accountsQuery = useMemberExternalAccounts(memberId);
  const createMutation = useCreateCalendarEventFromScratch();

  const [attendeeInput, setAttendeeInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const selectable = useMemo(
    () => (accountsQuery.data?.items ?? []).filter((a) => a.calendarWriteCapable && a.isActive),
    [accountsQuery.data?.items]
  );
  const selectableKey = selectable.map((a) => a.id).join(',');

  useEffect(() => {
    if (isOpen && draft.accountId == null && selectable.length > 0) {
      const def = selectable.find((a) => a.isDefaultCalendarWrite) ?? selectable[0];
      setDraft({ accountId: def.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, draft.accountId, selectableKey]);

  useEffect(() => {
    if (!isOpen) return;
    useViewContextStore.getState().setSlot('eventComposer', {
      title: draft.title,
      startAt: draft.startAt,
      endAt: draft.endAt,
      isAllDay: draft.isAllDay,
      location: draft.location,
      description: draft.description,
      attendees: draft.attendees.map((attendee) => attendee.email),
    });
    return () => useViewContextStore.getState().clearSlot('eventComposer');
  }, [
    isOpen,
    draft.title,
    draft.startAt,
    draft.endAt,
    draft.isAllDay,
    draft.location,
    draft.description,
    draft.attendees,
  ]);

  if (!isOpen) {
    return null;
  }

  const hasAccounts = selectable.length > 0;
  const selectedAccount = selectable.find((a) => a.id === draft.accountId) ?? null;
  const isCreating = createMutation.isPending;
  const containerClassName = composerContainerClassName(neoOpen);

  const patch = (next: Partial<typeof draft>): void => {
    if (error) {
      setError(null);
    }
    setDraft(next);
  };

  const handleAllDayToggle = (next: boolean): void => {
    const convert = (value: string): string => {
      if (!value) {
        return value;
      }
      if (next) {
        return value.slice(0, 10);
      }
      return value.length === 10 ? `${value}T09:00` : value;
    };
    patch({ isAllDay: next, startAt: convert(draft.startAt), endAt: convert(draft.endAt) });
  };

  const addAttendee = (): void => {
    const email = attendeeInput.trim().toLowerCase();
    if (!email) {
      return;
    }
    if (!isValidEmail(email)) {
      setError('Enter a valid email address for the attendee.');
      return;
    }
    if (draft.attendees.some((a) => a.email === email)) {
      setAttendeeInput('');
      return;
    }
    patch({ attendees: [...draft.attendees, { email, optional: false }] });
    setAttendeeInput('');
  };

  const removeAttendee = (index: number): void => {
    const attendees = draft.attendees.filter((_, i) => i !== index);
    patch({ attendees, sendInvite: attendees.length === 0 ? false : draft.sendInvite });
  };

  const toggleOptional = (index: number, optional: boolean): void => {
    patch({
      attendees: draft.attendees.map((a, i) => (i === index ? { ...a, optional } : a)),
    });
  };

  const titleValid = draft.title.trim().length > 0;
  const hasRange = draft.startAt.length > 0 && draft.endAt.length > 0;
  const rangeValid = hasRange
    ? draft.isAllDay
      ? draft.endAt >= draft.startAt
      : new Date(draft.endAt).getTime() > new Date(draft.startAt).getTime()
    : false;
  const canCreate =
    hasAccounts && draft.accountId != null && titleValid && hasRange && rangeValid && !isCreating;
  const willSendInvites = draft.sendInvite && draft.attendees.length > 0;
  const showOutlookNoInviteNote =
    selectedAccount?.provider === 'MICROSOFT' && draft.attendees.length > 0 && !draft.sendInvite;

  const handleCreate = async (): Promise<void> => {
    if (!canCreate) {
      return;
    }
    setError(null);
    const attendees = draft.attendees.map((a) => ({ email: a.email, optional: a.optional }));
    try {
      await createMutation.mutateAsync({
        memberExternalAccountId: draft.accountId ?? undefined,
        title: draft.title.trim(),
        ...(draft.location.trim() ? { location: draft.location.trim() } : {}),
        ...(draft.description.trim() ? { description: draft.description.trim() } : {}),
        startAt: localToIso(draft.startAt, draft.isAllDay),
        endAt: localToIso(draft.endAt, draft.isAllDay),
        isAllDay: draft.isAllDay,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(attendees.length > 0 ? { attendees, sendInvite: draft.sendInvite } : {}),
      });
      closeEventCompose();
      toast.success(willSendInvites ? 'Event created and invites sent' : 'Event created');
    } catch (createError) {
      const message = getAxiosErrorDisplayMessage(createError, 'Failed to create event.');
      setError(message);
      toast.error(message);
    }
  };

  const renderAccountSelector = () => {
    if (!hasAccounts) {
      return null;
    }
    if (selectable.length === 1) {
      return (
        <span
          className="truncate text-xs text-muted-foreground"
          title={describeAccount(selectable[0])}
        >
          {describeAccount(selectable[0])}
        </span>
      );
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={isCreating}
            className="inline-flex max-w-[16rem] items-center gap-1 truncate rounded-full bg-muted/60 px-3 py-1 text-xs hover:bg-muted disabled:opacity-60"
          >
            <span className="truncate">
              {selectedAccount ? describeAccount(selectedAccount) : 'Choose account'}
            </span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {selectable.map((account) => (
            <DropdownMenuItem key={account.id} onSelect={() => patch({ accountId: account.id })}>
              {describeAccount(account)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <section role="region" aria-label="New event composer" className={containerClassName}>
      <div className="flex items-center justify-between gap-2 rounded-t-lg border-b bg-muted/40 px-3 py-2">
        <button
          type="button"
          onClick={isMinimized ? restore : minimize}
          className="min-w-0 flex-1 text-left text-sm font-medium"
        >
          New event
        </button>
        <div className="flex items-center gap-1">
          {!isMinimized ? renderAccountSelector() : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={isMinimized ? 'Restore composer' : 'Minimize composer'}
            onClick={isMinimized ? restore : minimize}
          >
            {isMinimized ? <ChevronDown className="h-4 w-4 rotate-180" /> : <Minus className="h-4 w-4" />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Close composer"
            onClick={closeEventCompose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isMinimized ? null : !hasAccounts ? (
        <div className="space-y-3 p-4">
          <p className="text-sm text-muted-foreground">
            Connect a calendar account to create events.
          </p>
          <Button type="button" onClick={() => navigate('/settings/integrations')}>
            Connect account
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-3 px-3 py-2">
            <Input
              value={draft.title}
              onChange={(event) => patch({ title: event.target.value })}
              placeholder="Title"
              aria-label="Title"
              disabled={isCreating}
            />

            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="event-all-day" className="text-sm text-muted-foreground">
                All day
              </Label>
              <Switch
                id="event-all-day"
                checked={draft.isAllDay}
                onCheckedChange={handleAllDayToggle}
                disabled={isCreating}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="event-start" className="text-xs text-muted-foreground">
                  Start
                </Label>
                <Input
                  id="event-start"
                  type={draft.isAllDay ? 'date' : 'datetime-local'}
                  value={draft.startAt}
                  onChange={(event) => patch({ startAt: event.target.value })}
                  disabled={isCreating}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="event-end" className="text-xs text-muted-foreground">
                  End
                </Label>
                <Input
                  id="event-end"
                  type={draft.isAllDay ? 'date' : 'datetime-local'}
                  value={draft.endAt}
                  onChange={(event) => patch({ endAt: event.target.value })}
                  disabled={isCreating}
                />
              </div>
            </div>
            {hasRange && !rangeValid ? (
              <p className="text-xs text-destructive" role="alert">
                End must be after start.
              </p>
            ) : null}

            <Input
              value={draft.location}
              onChange={(event) => patch({ location: event.target.value })}
              placeholder="Location"
              aria-label="Location"
              disabled={isCreating}
            />
            <Textarea
              value={draft.description}
              onChange={(event) => patch({ description: event.target.value })}
              placeholder="Description"
              aria-label="Description"
              rows={4}
              disabled={isCreating}
              className="resize-none"
            />

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  value={attendeeInput}
                  onChange={(event) => setAttendeeInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addAttendee();
                    }
                  }}
                  placeholder="Add attendee email"
                  aria-label="Add attendee email"
                  disabled={isCreating}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Add attendee"
                  onClick={addAttendee}
                  disabled={isCreating}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {draft.attendees.length > 0 ? (
                <ul className="space-y-1">
                  {draft.attendees.map((attendee, index) => (
                    <li
                      key={attendee.email}
                      className="flex items-center justify-between gap-2 rounded-md border px-2 py-1 text-sm"
                    >
                      <span className="truncate" title={attendee.email}>
                        {attendee.email}
                      </span>
                      <div className="flex shrink-0 items-center gap-2">
                        <label className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Checkbox
                            checked={attendee.optional}
                            onCheckedChange={(value) => toggleOptional(index, value === true)}
                            disabled={isCreating}
                          />
                          Optional
                        </label>
                        <button
                          type="button"
                          aria-label={`Remove ${attendee.email}`}
                          onClick={() => removeAttendee(index)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
              {draft.attendees.length > 0 ? (
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="event-send-invite" className="text-sm text-muted-foreground">
                    Email an invitation to attendees
                  </Label>
                  <Switch
                    id="event-send-invite"
                    checked={draft.sendInvite}
                    onCheckedChange={(value) => patch({ sendInvite: value })}
                    disabled={isCreating}
                  />
                </div>
              ) : null}
              {showOutlookNoInviteNote ? (
                <p className="text-xs text-muted-foreground">
                  Outlook can&apos;t add guests without notifying them. These attendees are saved in
                  Vivport; they won&apos;t appear on the Outlook event unless you send the invitation.
                </p>
              ) : null}
            </div>
          </div>

          {error ? (
            <p className="px-3 pb-1 text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex items-center gap-2 border-t px-3 py-2">
            <Button type="button" onClick={() => void handleCreate()} disabled={!canCreate}>
              {isCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {willSendInvites ? 'Create & send invites' : 'Create event'}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
